import { useGlobalContext } from 'GlobalContext';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { searchDocument } from 'services/minhaReceita';
import { createResellerCustomer } from 'services/reseller';
import { loadSoftwaresAndServices } from 'services/software';
import { searchViaCep } from 'services/zipCode';
import { validateCNPJ, validateCPF } from 'shared/helpers/document';
import { validateEmail } from 'shared/helpers/email';
import { onlyDocumentChars } from 'shared/helpers/string';
import { Service } from 'shared/models/service';
import { Software } from 'shared/models/software';
import { Periodicity } from 'shared/types/periodicity';
import { useResellerContext } from '../../ResellerContext';
import CreateResellerCustomerModal from './CreateResellerCustomerModal';

export interface CustomerErrors {
  cnpj?: string;
  name?: string;
  contact_email?: string;
  trade_name?: string;
  state_registration?: string;
  city_registration?: string;
  postal_code?: string;
  logradouro?: string;
  numero?: string;
  bairro?: string;
  telephone?: string;
}

export interface ContractErrors {
  due_date?: string;
  services?: string;
}

export interface InstallationErrors {
  installationDocument?: string;
  password?: string;
  confirmPassword?: string;
  negocio?: string;
}

interface ModalContextData {
  step: number;
  setStep(n: number): void;
  nextStep(): void;
  previousStep(): void;
  loading: boolean;

  // Customer fields
  cnpj: string;
  setCnpj(s: string): void;
  name: string;
  setName(s: string): void;
  contact_email: string;
  setContactEmail(s: string): void;
  trade_name: string;
  setTradeName(s: string): void;
  state_registration: string;
  city_registration: string;
  postal_code: string;
  setPostalCode(s: string): void;
  logradouro: string;
  setAddress(s: string): void;
  numero: string;
  setNumber(s: string): void;
  complemento: string;
  setComplement(s: string): void;
  bairro: string;
  setNeighborhood(s: string): void;
  telephone: string;
  setPhone(s: string): void;
  invalidCep: boolean;
  customerErrors: CustomerErrors;

  // Contract fields
  paymentDay: number;
  setPaymentDay(n: number): void;
  periodicity: Periodicity;
  setPeriodicity(p: Periodicity): void;
  product?: Software;
  services: Service[];
  selectedServiceIds: number[];
  addService(id: number): void;
  removeService(id: number): void;
  contractErrors: ContractErrors;

  // Installation fields
  installationDocument: string;
  setInstallationDocument(s: string): void;
  password: string;
  setPassword(s: string): void;
  confirmPassword: string;
  setConfirmPassword(s: string): void;
  negocio: string;
  setNegocio(s: string): void;
  installationErrors: InstallationErrors;

  validateCustomerStep(): boolean;
  validateContractStep(): boolean;
  handleSubmit(): void;
}

const Context = createContext<ModalContextData>({} as ModalContextData);

export const useCreateResellerCustomerModalContext = () => useContext(Context);

interface Props {
  isOpen: boolean;
  onClose(): void;
}

const CreateResellerCustomerModalContext: React.FC<Props> = ({
  isOpen,
  onClose,
}) => {
  const { confirm } = useGlobalContext();
  const { reload } = useResellerContext();

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Customer
  const [cnpj, setCnpj] = useState('');
  const [name, setName] = useState('');
  const [contact_email, setContactEmail] = useState('');
  const [trade_name, setTradeName] = useState('');
  const [state_registration, setState] = useState('');
  const [city_registration, setCity] = useState('');
  const [postal_code, setPostalCode] = useState('');
  const [logradouro, setAddress] = useState('');
  const [numero, setNumber] = useState('');
  const [complemento, setComplement] = useState('');
  const [bairro, setNeighborhood] = useState('');
  const [telephone, setPhone] = useState('');
  const [invalidCep, setInvalidCep] = useState(false);
  const [customerErrors, setCustomerErrors] = useState<CustomerErrors>({});

  // Contract
  const [paymentDay, setPaymentDay] = useState(5);
  const [periodicity, setPeriodicity] = useState<Periodicity>('yearly');
  const [product, setProduct] = useState<Software>();
  const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([]);
  const [contractErrors, setContractErrors] = useState<ContractErrors>({});

  // Installation
  const [installationDocument, setInstallationDocument] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [negocio, setNegocio] = useState('');
  const [installationErrors, setInstallationErrors] =
    useState<InstallationErrors>({});

  const nextStep = () => setStep((s) => s + 1);
  const previousStep = () => setStep((s) => s - 1);

  const addService = (id: number) => {
    if (!selectedServiceIds.includes(id)) {
      setSelectedServiceIds([...selectedServiceIds, id]);
    }
  };

  const removeService = (id: number) => {
    setSelectedServiceIds(selectedServiceIds.filter((sid) => sid !== id));
  };

  const filteredServices = product
    ? (product.services ?? []).filter(
        (s: Service) => s.periodicity === periodicity,
      )
    : [];

  useEffect(() => {
    if (!isOpen) return;
    loadSoftwaresAndServices()
      .then((response) => {
        const softwares =
          response.data?.results?.map((s) => {
            const sw = new Software(s);
            sw.services = sw.services.filter((svc) =>
              svc.name?.toLowerCase().includes('revenda'),
            );
            return sw;
          }) ?? [];
        const withServices = softwares.filter((sw) => sw.services.length > 0);
        if (withServices.length) {
          setProduct(withServices[0]);
        }
      })
      .catch(() => toast.error('Falha ao carregar serviços'));
  }, [isOpen]);

  // ViaCEP lookup
  const searchZipCode = useCallback(() => {
    if (!postal_code) {
      setInvalidCep(false);
      return;
    }
    if (postal_code.length < 9) {
      setInvalidCep(false);
      return;
    }
    if (postal_code.length === 9) {
      searchViaCep(postal_code)
        .then(({ data }) => {
          if (data.erro) {
            setInvalidCep(true);
            setCity('');
            setState('');
            return;
          }
          setInvalidCep(false);
          setAddress(data.logradouro);
          setNeighborhood(data.bairro);
          setCity(data.localidade);
          setState(data.uf);
        })
        .catch(() => {
          setInvalidCep(true);
          setCity('');
          setState('');
        });
    }
  }, [postal_code]);

  useEffect(() => {
    searchZipCode();
  }, [searchZipCode]);

  // MinhaReceita auto-fill
  const searchMinhaReceitaInfo = useCallback(() => {
    if (!cnpj) return;
    if (
      (validateCPF(onlyDocumentChars(cnpj)) ||
        validateCNPJ(onlyDocumentChars(cnpj))) &&
      cnpj.length >= 14
    ) {
      searchDocument(cnpj)
        .then(({ data }) => {
          if (data.razao_social) setTradeName(data.razao_social);
          if (data.nome_fantasia) setName(data.nome_fantasia);
          if (data.email) setContactEmail(data.email);
          if (data.ddd_telefone_1) setPhone(data.ddd_telefone_1);
          if (data.cep)
            setPostalCode(`${data.cep.slice(0, 5)}-${data.cep.slice(-3)}`);
          if (data.logradouro)
            setAddress(
              `${data.descricao_tipo_de_logradouro} ${data.logradouro}`,
            );
          if (data.numero) setNumber(data.numero);
          if (data.complemento) setComplement(data.complemento);
          if (data.bairro) setNeighborhood(data.bairro);
          if (data.municipio) setCity(data.municipio);
          if (data.uf) setState(data.uf);
        })
        .catch(() => {});
    }
  }, [cnpj]);

  useEffect(() => {
    searchMinhaReceitaInfo();
  }, [searchMinhaReceitaInfo]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setStep(0);
      setCnpj('');
      setName('');
      setContactEmail('');
      setTradeName('');
      setState('');
      setCity('');
      setPostalCode('');
      setAddress('');
      setNumber('');
      setComplement('');
      setNeighborhood('');
      setPhone('');
      setInvalidCep(false);
      setCustomerErrors({});
      setPaymentDay(5);
      setPeriodicity('yearly');
      setSelectedServiceIds([]);
      setContractErrors({});
      setInstallationDocument('');
      setPassword('');
      setConfirmPassword('');
      setNegocio('');
      setInstallationErrors({});
      setLoading(false);
    }
  }, [isOpen]);

  const validateCustomerStep = (): boolean => {
    const errors: CustomerErrors = {};
    const doc = onlyDocumentChars(cnpj);

    if (!doc) errors.cnpj = 'Documento é obrigatório';
    else if (!validateCNPJ(doc) && !validateCPF(doc))
      errors.cnpj = 'Insira um documento válido';

    if (!name) errors.name = 'Nome fantasia é obrigatório';
    if (!contact_email) errors.contact_email = 'Email é obrigatório';
    else if (!validateEmail(contact_email))
      errors.contact_email = 'Insira um email válido';
    if (!trade_name) errors.trade_name = 'Razão social é obrigatório';
    if (!state_registration) errors.state_registration = 'UF é obrigatório';
    else if (state_registration.length !== 2)
      errors.state_registration = 'Insira uma UF válida';
    if (!city_registration) errors.city_registration = 'Cidade é obrigatório';
    if (!postal_code) errors.postal_code = 'CEP é obrigatório';
    else if (postal_code.length !== 9)
      errors.postal_code = 'Insira um CEP válido';
    if (!logradouro) errors.logradouro = 'Logradouro é obrigatório';
    if (!numero) errors.numero = 'Número é obrigatório';
    if (!bairro) errors.bairro = 'Bairro é obrigatório';

    const phoneDigits = telephone.replace(/\D/g, '');
    if (!phoneDigits) errors.telephone = 'Telefone é obrigatório';
    else if (phoneDigits.length !== 10 && phoneDigits.length !== 11)
      errors.telephone = 'Insira um telefone válido';

    setCustomerErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateContractStep = (): boolean => {
    const errors: ContractErrors = {};
    if (!paymentDay) errors.due_date = 'Dia de vencimento é obrigatório';
    if (!selectedServiceIds.length)
      errors.services = 'Selecione ao menos um serviço';
    setContractErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateInstallationStep = (): boolean => {
    const errors: InstallationErrors = {};
    const doc = onlyDocumentChars(installationDocument);
    if (!doc) errors.installationDocument = 'Documento é obrigatório';
    else if (!validateCNPJ(doc) && !validateCPF(doc))
      errors.installationDocument = 'Insira um documento válido';
    if (!password) errors.password = 'Senha é obrigatória';
    else if (password.length < 6)
      errors.password = 'Senha deve ter ao menos 6 caracteres';
    if (!confirmPassword) errors.confirmPassword = 'Confirmação é obrigatória';
    else if (password !== confirmPassword)
      errors.confirmPassword = 'As senhas não conferem';
    if (!negocio) errors.negocio = 'Tipo do estabelecimento é obrigatório';
    setInstallationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateInstallationStep()) return;
    if (loading) return;

    const payload = {
      customer: {
        cnpj: onlyDocumentChars(cnpj),
        name: name.trim(),
        contact_email: contact_email.trim(),
        trade_name: trade_name.trim(),
        state_registration: state_registration.trim(),
        city_registration: city_registration.trim(),
        postal_code: postal_code.trim(),
        logradouro: logradouro.trim(),
        numero: numero.trim(),
        complemento: complemento.trim(),
        bairro: bairro.trim(),
        telephone: telephone.replace(/\D/g, ''),
      },
      contract: {
        due_date: (() => {
          const today = new Date();
          const dueDate = new Date(
            today.getFullYear(),
            today.getMonth(),
            paymentDay,
          );
          if (paymentDay < today.getDate()) {
            dueDate.setMonth(dueDate.getMonth() + 1);
          }
          const y = dueDate.getFullYear();
          const m = String(dueDate.getMonth() + 1).padStart(2, '0');
          const d = String(dueDate.getDate()).padStart(2, '0');
          return `${y}-${m}-${d} 00:00:00`;
        })(),
        periodicity,
        services: selectedServiceIds.map((id) => ({ service_id: id })),
      },
      installation: {
        document: onlyDocumentChars(installationDocument),
        password,
        negocio,
        permission: null,
      },
    };

    confirm('Criar cliente revendedor', 'Deseja continuar?', async () => {
      setLoading(true);
      try {
        await createResellerCustomer(payload);
        toast.success('Cliente criado com sucesso');
        onClose();
        reload();
      } catch {
        toast.error('Falha ao criar cliente');
      } finally {
        setLoading(false);
      }
    });
  };

  const data: ModalContextData = {
    step,
    setStep,
    nextStep,
    previousStep,
    loading,
    cnpj,
    setCnpj,
    name,
    setName,
    contact_email,
    setContactEmail,
    trade_name,
    setTradeName,
    state_registration,
    city_registration,
    postal_code,
    setPostalCode,
    logradouro,
    setAddress,
    numero,
    setNumber,
    complemento,
    setComplement,
    bairro,
    setNeighborhood,
    telephone,
    setPhone,
    invalidCep,
    customerErrors,
    paymentDay,
    setPaymentDay,
    periodicity,
    setPeriodicity,
    product,
    services: filteredServices,
    selectedServiceIds,
    addService,
    removeService,
    contractErrors,
    installationDocument,
    setInstallationDocument,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    negocio,
    setNegocio,
    installationErrors,
    validateCustomerStep,
    validateContractStep,
    handleSubmit,
  };

  return (
    <Context.Provider value={data}>
      <CreateResellerCustomerModal isOpen={isOpen} onClose={onClose} />
    </Context.Provider>
  );
};

export default CreateResellerCustomerModalContext;
